import { useHistory } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Stepper, Step, StepLabel, StepContent, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, CircularProgress, Alert, Chip, Box, Typography, LinearProgress, TextField, MenuItem, } from '@mui/material';

import { Download as DownloadIcon, CheckCircle as SuccessIcon, Error as ErrorIcon, ArrowBack as BackIcon } from '@mui/icons-material';

import constants from '../constants';
import { initiateExport, executeExport, clearExportData } from '../store/Objects';

const ExportWizard = ({ objectType: initialObjectType, exportWizardOpen, setExportWizardOpen, selectedIds = [] }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [activeObject, setActiveObject] = useState(initialObjectType || 'contacts');
  const objectData = useSelector(state => state['Objects']?.[activeObject]);
  const loading = objectData?.['loading'];
  const exportStatus = objectData?.['exportStatus'];
  const exportPreview = objectData?.['exportPreview'];
  const objectSchema = constants.getObjectSchema(activeObject);

  const [jobId, setJobId] = useState(null);
  const [format, setFormat] = useState('csv');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFields, setSelectedFields] = useState([]);
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');

  // Initialize export when opened
  useEffect(() => {
    if (exportWizardOpen) {
      setJobId(null);
      setActiveStep(0);
      setFormat('csv');
      setSelectedFields([]);
      setActiveObject(initialObjectType || 'contacts');
      dispatch(clearExportData({ objectType: activeObject }));
    }
  }, [exportWizardOpen]);

  // Initiate export when entering field selection step (after format is selected)
  useEffect(() => {
    if (exportWizardOpen && activeStep === 2 && !jobId) {
      dispatch(initiateExport({
        objectType: activeObject,
        config: { selectedIds, format },
        successCallback: (data) => {
          setJobId(data['jobId']);
          // Auto-select all standard fields
          const standardFields = data['availableFields']?.filter(f => f['type'] === 'standard').map(f => f['key']) || [];
          setSelectedFields(standardFields);
        }
      }));
    }
  }, [activeStep, activeObject, exportWizardOpen, format]);

  const handleNext = () => setActiveStep((prevStep) => prevStep + 1);
  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  const handleClose = () => {
    setExportWizardOpen(false);
    dispatch(clearExportData({ objectType: activeObject }));
  };

  const handleFieldToggle = (fieldKey) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldKey)) return prev.filter(f => f !== fieldKey);
      return [...prev, fieldKey];
    });
  };

  const handleSelectAllFields = (group) => {
    const groupFields = exportPreview?.['availableFields']?.filter(f => f['group'] === group).map(f => f['key']) || [];
    setSelectedFields(prev => {
      const hasAll = groupFields.every(f => prev.includes(f));
      if (hasAll) return prev.filter(f => !groupFields.includes(f));
      return [...new Set([...prev, ...groupFields])];
    });
  };

  const executeExportJob = () => {
    if (!jobId || selectedFields.length === 0) return;

    setActiveStep(4); // Move to progress step
    dispatch(executeExport({
      objectType: activeObject,
      jobId,
      config: { selectedFields, format, includeHeaders, dateFormat },
      successCallback: () => {
        setActiveStep(5); // Move to complete step
      },
      failCallback: () => {
        setActiveStep(5); // Move to complete step (will show error)
      }
    }));
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0: // Select Object
        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>What data do you want to export?</h4>
              <p>Select the type of data you want to export.</p>
            </div>
            <div className='object-list-area'>
              {Object.values(constants['objectsSchema'])['map']((box, index) => (
                <div
                  key={index}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setActiveObject(box['type'])}
                  className={`object-list-item ${activeObject === box['type'] ? 'active' : ''}`}
                >
                  <span className={`custom-checkbox ${activeObject === box['type'] ? 'checked' : ''}`} />
                  <div className='img-box'> <img alt={box['types']} src={require(`../static/img/${box['image']}`)} /></div>
                  <div className='text-box'>
                    <h5>{box['plural']}</h5>
                    <p>{box['description']}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 1: // Format Selection
        return (
          <>
            <div className='object-selection'>
              <div className='title-import-modal'>
                <h4>Select Export Format</h4>
              </div>
              <div className='object-list-area'>
                {Object.values(constants['exportFormats']).map((formatItem, index) => (
                  <div
                    key={index}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFormat(prev => prev === formatItem.value ? '' : formatItem.value)}
                    className={`object-list-item ${format === formatItem.value ? 'active' : ''}`}
                  >
                    <span className={`custom-checkbox ${format === formatItem.value ? 'checked' : ''}`} />
                    <div className='img-box'> <img alt={formatItem['types']} src={require(`../static/img/${formatItem['image']}`)} /></div>
                    <div className='text-box'>
                      <h5>{formatItem['label']}</h5>
                      <p>{formatItem['description']}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )

      case 2: // Field Selection
        const fieldGroups = exportPreview?.['availableFields']?.reduce((acc, field) => {
          if (!acc[field['group']]) acc[field['group']] = [];
          acc[field['group']].push(field);
          return acc;
        }, {}) || {};

        // Show loading state while fields are being fetched
        if (loading || !exportPreview || Object.keys(fieldGroups).length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading available fields...
              </Typography>
            </Box>
          );
        }

        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>Select fields to include in export</h4>
            </div>
            <div className='export-checked-area'>
              {Object.entries(fieldGroups).map(([group, fields]) => {
                const allSelected = fields.every(f => selectedFields.includes(f['key']));
                const someSelected = fields.some(f => selectedFields.includes(f['key']));

                return (
                  <Box key={group} sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={() => handleSelectAllFields(group)}
                        />
                      }
                      label={<Typography variant="subtitle1" fontWeight="bold">{group}</Typography>}
                    />
                    <FormGroup sx={{ ml: 3 }}>
                      {fields.map(field => (
                        <FormControlLabel
                          key={field['key']}
                          control={
                            <Checkbox
                              checked={selectedFields.includes(field['key'])}
                              onChange={() => handleFieldToggle(field['key'])}
                            />
                          }
                          label={field['label']}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                );
              })}

              {selectedFields.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please select at least one field to export
                </Alert>
              )}
            </div>
          </div>
        )

      case 3: // Export Options
        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>Configure Export Options</h4>
            </div>
            <div className='export-checked-area'>
              <div className='group-form'>
                {format === 'csv' && (
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={includeHeaders} onChange={(e) => setIncludeHeaders(e.target.checked)} />}
                      label="Include column headers"
                    />
                  </FormGroup>
                )}
              </div>
              <div className='group-form'>
                <TextField
                  select
                  fullWidth
                  label="Date Format"
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  margin="normal"
                >
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (2024-01-15)</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (01/15/2024)</MenuItem>
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (15/01/2024)</MenuItem>
                  <MenuItem value="MMMM D, YYYY">MMMM D, YYYY (January 15, 2024)</MenuItem>
                </TextField>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Export Summary</Typography>
                  <Typography variant="body2">Format: <strong>{format.toUpperCase()}</strong></Typography>
                  <Typography variant="body2">Fields: <strong>{selectedFields.length} selected</strong></Typography>
                  <Typography variant="body2">Records: <strong>{exportPreview?.['totalRecords'] || 0}</strong></Typography>
                  <Typography variant="body2">
                    Estimated size: <strong>{formatFileSize(exportPreview?.['estimatedSize'] || 0)}</strong>
                  </Typography>
                </Box>
              </div>
            </div>
          </div>
        )

      case 4: // Progress
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Exporting {objectSchema['plural']}...
            </Typography>

            {exportStatus && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={exportStatus['progress']?.['percentComplete'] || 0}
                  sx={{ mt: 2, mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {exportStatus['progress']?.['processedRecords'] || 0} / {exportStatus['progress']?.['totalRecords'] || 0} records processed
                </Typography>
              </>
            )}
          </Box>
        );

      case 5: // Complete
        const isSuccess = exportStatus?.['status'] === 'completed';
        const isError = exportStatus?.['status'] === 'failed';

        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {isSuccess && (
              <>
                <SuccessIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Export Completed Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {exportStatus['results']?.['recordsExported'] || 0} records exported
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  File size: {formatFileSize(exportStatus['results']?.['fileSize'] || 0)}
                </Typography>
                <button
                 className="btn-style-new"
                  startIcon={<DownloadIcon />}
                  sx={{ mt: 2 }}
                  onClick={() => window.open(exportStatus['results']?.['downloadUrl'], '_blank')}
                >
                  Download {format.toUpperCase()} File
                </button>
                <button
                  variant="outlined"
                  sx={{ mt: 2, ml: 2 }}
                  onClick={() => {
                    handleClose();
                    // Navigate to export history
                    history.push('/settings?tab=import-export');
                  }}
                  className="btn-style-border"
                >
                  View Export History
                </button>
              </>
            )}

            {isError && (
              <>
                <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom color="error">
                  Export Failed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {exportStatus['error']?.['message'] || 'An error occurred during export'}
                </Typography>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const steps = ['Select Object', 'Select Format', 'Choose Fields', 'Export Options', 'Processing', 'Complete'];
  const canProceed = activeStep === 0 || activeStep === 1 || (activeStep === 2 && selectedFields.length > 0) || activeStep === 3;

  return (
    <>
      <div isOpen={exportWizardOpen} className='single-agent-page single-contact-page import-page'>
        <div className='page-header-new style-two'>
          <h2>
            <div className='right-heading-area'>
              <h2>
                <button className="icon-btn-back" onClick={handleClose}>
                  <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                  </svg>
                </button>
                Export {objectSchema['plural']} </h2>
            </div>
          </h2>
        </div>
        <div className='bottom-content-area contact-details-area import-wizard-popup'>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {getStepContent(activeStep)}
        </div>
        <div className='bottom-btns-area'>
          {activeStep < 5 && (
            <>
              <Button
                onClick={handleBack}
                startIcon={<BackIcon />}
                className='btn-style-border'
                disabled={activeStep === 0}
              >
                Back
              </Button>
              <Button className='btn-cancel-style' onClick={handleClose}>Close</Button>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={activeStep === 3 ? executeExportJob : handleNext}
                disabled={!canProceed || loading}
                className='btn-style-new'
              >
                {activeStep === 3 ? 'Start Export' : 'Next'}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ExportWizard;